package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.model.ChecklistItem
import com.evoapps.evoshift.domain.model.InvalidChecklistException
import com.evoapps.evoshift.domain.model.KioskException
import com.evoapps.evoshift.domain.model.KioskOfflineException
import com.evoapps.evoshift.domain.model.KioskPerson
import com.evoapps.evoshift.domain.model.ShiftChecklist
import com.evoapps.evoshift.domain.repository.KioskChecklistMoment
import com.evoapps.evoshift.domain.repository.KioskChecklistRepository
import com.evoapps.evoshift.domain.usecase.checklist.tick
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.checklist_note_required
import evoshift.composeapp.generated.resources.kiosk_checklist_needs_connection
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.Instant

/** SH-127: the checklists the tablet shows one person, over the PIN pad or the confirmation. */
data class KioskChecklistState(
    val person: KioskPerson,
    val moment: KioskChecklistMoment,
    val checklists: List<ShiftChecklist>,
    val noteDraft: ChecklistNoteDraft? = null,
    /** A tick could not reach the server: ticks need a connection, skipping still works. */
    val offline: Boolean = false,
    /** Skipping or finishing is on its way. */
    val isClosing: Boolean = false,
    val error: UiText? = null
) {
    val isComplete: Boolean get() = checklists.all { it.isComplete }
}

/** How the person left the checklists: finished, or skipped ([skipSaved]: the skip reached the server). */
data class KioskChecklistExit(val skipped: Boolean, val skipSaved: Boolean = true)

/**
 * SH-127: one person's checklists on the tablet, opened by [KioskViewModel] before a clock-out
 * (the clock-out waits for "End shift" or "Skip and end shift") or after a clock-in (the opening
 * ones). Every call carries the person's PIN to the kioskChecklist function; the PIN lives here
 * only while the checklists are on screen. A tick shows at once and is taken back if the server
 * refuses it; without a connection the screen says that ticks need one, and skipping still lets
 * the person go on (the clock-out then waits in the offline queue as before).
 */
class KioskChecklistSession(
    private val repository: KioskChecklistRepository,
    private val scope: CoroutineScope,
    private val now: () -> Instant,
    /** Any touch: restarts the tablet's inactivity timer. */
    private val onActivity: () -> Unit = {},
    /** A refusal the tablet must act on (an unpaired tablet signs out). */
    private val onRefused: (Throwable) -> Unit = {}
) {
    /** What [open] did. */
    sealed interface Opened {
        /** The checklists are on screen; [close] runs when the person is done with them. */
        data object Shown : Opened

        /** Nothing to tick. */
        data object None : Opened

        /** The server could not be reached. */
        data object Offline : Opened

        data class Refused(val error: Throwable) : Opened
    }

    private val _state = MutableStateFlow<KioskChecklistState?>(null)
    val state: StateFlow<KioskChecklistState?> = _state.asStateFlow()

    private var pin: String? = null
    private var shiftId: String? = null
    private var onClose: (suspend (KioskChecklistExit) -> Unit)? = null
    private val sending = Mutex()

    /** Asks the server for [person]'s checklists of [shiftId] at [moment] and shows them, if any. */
    suspend fun open(
        person: KioskPerson,
        pin: String,
        shiftId: String,
        moment: KioskChecklistMoment,
        onClose: suspend (KioskChecklistExit) -> Unit
    ): Opened {
        val listed = repository.list(person.userId, pin, shiftId, moment)
        val checklists = listed.getOrElse { e ->
            return if (e is KioskOfflineException) Opened.Offline else Opened.Refused(e)
        }
        if (checklists.isEmpty()) return Opened.None
        this.pin = pin
        this.shiftId = shiftId
        this.onClose = onClose
        _state.value = KioskChecklistState(person, moment, checklists)
        return Opened.Shown
    }

    /** Ticks or unticks [item]; an item that needs a note opens the note field first. */
    fun toggle(checklistId: String, item: ChecklistItem) {
        val checklist = find(checklistId) ?: return
        onActivity()
        val done = !checklist.isDone(item.id)
        if (done && item.requiresNote) {
            _state.update { it?.copy(noteDraft = ChecklistNoteDraft(checklistId, item, checklist.noteOf(item.id).orEmpty())) }
            return
        }
        send(checklist, item, done, checklist.noteOf(item.id))
    }

    fun editNote(checklistId: String, item: ChecklistItem) {
        val checklist = find(checklistId) ?: return
        onActivity()
        _state.update { it?.copy(noteDraft = ChecklistNoteDraft(checklistId, item, checklist.noteOf(item.id).orEmpty())) }
    }

    fun updateNote(text: String) {
        onActivity()
        _state.update { it?.copy(noteDraft = it.noteDraft?.copy(note = text)) }
    }

    fun dismissNote() = _state.update { it?.copy(noteDraft = null) }

    /** Saves the note and ticks the item (a note is what was done). */
    fun saveNote() {
        val draft = _state.value?.noteDraft ?: return
        val checklist = find(draft.checklistId) ?: return
        if (draft.item.requiresNote && draft.note.isBlank()) {
            _state.update { it?.copy(error = UiText.Resource(Res.string.checklist_note_required)) }
            return
        }
        _state.update { it?.copy(noteDraft = null) }
        send(checklist, draft.item, done = true, note = draft.note)
    }

    fun messageShown() = _state.update { it?.copy(error = null) }

    /** Everything is ticked: go on (end the shift, or back to the grid). */
    fun finish() {
        val state = _state.value ?: return
        if (!state.isComplete || state.isClosing) return
        close(KioskChecklistExit(skipped = false))
    }

    /** Goes on without finishing; the skip is recorded on the server (when it can be reached). */
    fun skip() {
        val state = _state.value ?: return
        if (state.isClosing) return
        val userId = state.person.userId
        val pin = pin ?: return
        val shiftId = shiftId ?: return
        val open = state.checklists.filterNot { it.isComplete }.map { it.templateId }
        _state.update { it?.copy(isClosing = true, noteDraft = null) }
        scope.launch {
            val saved = if (open.isEmpty()) Result.success(Unit) else sending.withLock { repository.skip(userId, pin, shiftId, open) }
            saved.exceptionOrNull()?.let { if (it !is KioskOfflineException) onRefused(it) }
            close(KioskChecklistExit(skipped = true, skipSaved = saved.isSuccess))
        }
    }

    /** Forgets the person and their PIN (timeout, or back to the grid). */
    fun clear() {
        _state.value = null
        pin = null
        shiftId = null
        onClose = null
    }

    private fun close(exit: KioskChecklistExit) {
        val next = onClose
        clear()
        if (next != null) scope.launch { next(exit) }
    }

    private fun find(checklistId: String): ShiftChecklist? = _state.value?.checklists?.firstOrNull { it.id == checklistId }

    private fun send(checklist: ShiftChecklist, item: ChecklistItem, done: Boolean, note: String?) {
        val state = _state.value ?: return
        val pin = pin ?: return
        val shiftId = shiftId ?: return
        val ticked = try {
            checklist.tick(item.id, done, note, now())
        } catch (e: InvalidChecklistException) {
            _state.update { it?.copy(error = UiText.Resource(Res.string.checklist_note_required)) }
            return
        }
        _state.update { s -> s?.copy(checklists = s.checklists.map { if (it.id == ticked.id) ticked else it }, error = null) }
        scope.launch {
            val result = sending.withLock {
                repository.tick(state.person.userId, pin, shiftId, checklist.templateId, item.id, done, ticked.noteOf(item.id))
            }
            val error = result.exceptionOrNull() ?: run {
                _state.update { it?.copy(offline = false) }
                return@launch
            }
            // Taken back: only this item, other ticks may have gone through meanwhile.
            _state.update { s ->
                s?.copy(
                    checklists = s.checklists.map { c ->
                        if (c.id != checklist.id) c
                        else c.copy(
                            checks = checklist.checks[item.id]?.let { c.checks + (item.id to it) } ?: (c.checks - item.id),
                            completedAt = checklist.completedAt
                        )
                    },
                    offline = error is KioskOfflineException || s.offline,
                    error = if (error is KioskOfflineException) UiText.Resource(Res.string.kiosk_checklist_needs_connection) else error.toUiText()
                )
            }
            if (error is KioskException) onRefused(error)
        }
    }
}
